import { useForm } from 'react-hook-form';
import { CurrencyInput } from '../components/fields/CurrencyInput';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { TextField } from '../components/fields/TextField';
import { SelectField } from '../components/fields/SelectField';
import { RadioGroup } from '../components/fields/RadioGroup';
import { PhoneInput } from '../components/fields/PhoneInput';
import { CityAutocomplete } from '../components/fields/CityAutocomplete';
import { EmailInput } from '../components/fields/EmailInput';
import { NameInput } from '../components/fields/NameInput';
import { useFormStore, useNavigation } from '../store/formStore';
import { personalStepSchema } from '../utils/validation';
import { getCountryOptions } from '../config/countries';

interface PersonalInfoFormData {
  // Informations personnelles
  title: string;
  firstName: string;
  lastName: string;
  birthName: string;
  email: string;
  phone: string;
  birthDate: string;
  birthCity: string;
  birthDepartment: string;
  birthCountry: string;
  nationality: string;

  // Informations professionnelles
  professionCode: string;
  contractTypeCode: string;
  monthlySalary: number;
  companyName: string;
  seniorityMonths: number;
  otherIncome: number;
  contractEndDate?: string;
  companyCity?: string;
  companyPostalCode?: string;
  companyRegionCode?: string;
  retirementPension?: number;
  retirementPensionPeriodicity?: string;
  disabilityPension?: number;
  disabilityPensionPeriodicity?: string;
  reversionaryPension?: number;
  reversionaryPensionPeriodicity?: string;
}

export function PersonalInfoStep() {
  const { formData, updateField, errors } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors: formErrors, isValid, touchedFields }
  } = useForm<PersonalInfoFormData>({
    resolver: zodResolver(personalStepSchema),
    defaultValues: {
      // Informations personnelles
      title: formData.title || '',
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      birthName: formData.birthName || '',
      email: formData.email || '',
      phone: formData.phone || '',
      birthDate: formData.birthDate || '',
      birthCity: formData.birthCity || '',
      birthDepartment: formData.birthDepartment || '',
      birthCountry: formData.birthCountry || 'FR',
      nationality: formData.nationality || 'FR',

      // Informations professionnelles
      professionCode: formData.professionCode || '',
      contractTypeCode: formData.contractTypeCode || 'CDI',
      monthlySalary: formData.monthlySalary ?? ('' as unknown as number),
      companyName: formData.companyName || '',
      seniorityMonths: formData.seniorityMonths || ('' as unknown as number),
      otherIncome: formData.otherIncome || 0,
      contractEndDate: formData.contractEndDate || '',
      companyCity: formData.companyCity || '',
      companyPostalCode: formData.companyPostalCode || '',
      companyRegionCode: formData.companyRegionCode || '',
      retirementPension: formData.retirementPension || 0,
      retirementPensionPeriodicity: formData.retirementPensionPeriodicity || 'M',
      disabilityPension: formData.disabilityPension || 0,
      disabilityPensionPeriodicity: formData.disabilityPensionPeriodicity || 'M',
      reversionaryPension: formData.reversionaryPension || 0,
      reversionaryPensionPeriodicity: formData.reversionaryPensionPeriodicity || 'M'
    },
    mode: 'onChange'
  });
  
  useEffect(() => { trigger(); }, []);

  // N'affiche une erreur que si le champ a été touché par l'utilisateur
  const fieldError = (name: keyof PersonalInfoFormData): string | undefined =>
    touchedFields[name] ? formErrors[name]?.message : undefined;

  const touchField = (name: keyof PersonalInfoFormData) => () =>
    setValue(name, watch(name) as any, { shouldTouch: true, shouldValidate: true });

  // birthCityVerified est mis à true uniquement quand l'utilisateur sélectionne dans l'autocomplete
  // — on ne fait pas confiance au localStorage seul pour éviter les saisies libres non validées
  const [birthCityConfirmed, setBirthCityConfirmed] = useState(
    !!(formData.birthCity && formData.birthDepartment && formData.birthCityVerified)
  );
  const [companyCityConfirmed, setCompanyCityConfirmed] = useState(
    !!(formData.companyCity)
  );
  const [isBirthCountryFR, setIsBirthCountryFR] = useState(
    (formData.birthCountry || 'FR') === 'FR'
  );

  const watchedTitle = watch('title');
  const watchedProfessionCodeNum = parseInt(String(formData.professionCode ?? '0'), 10) || 0;

  // Ancienneté max : l'emprunteur ne peut pas avoir commencé à travailler avant ses 16 ans
  const birthDate = formData.birthDate ? new Date(formData.birthDate) : null;
  const ageInMonths = birthDate
    ? (new Date().getFullYear() - birthDate.getFullYear()) * 12 + (new Date().getMonth() - birthDate.getMonth())
    : 720;
  const maxSeniorityMonths = Math.max(0, ageInMonths - 16 * 12);
  const watchedContractTypeCode = watch('contractTypeCode');
  const watchedSeniorityMonths = watch('seniorityMonths');
  const watchedMonthlySalary = watch('monthlySalary');
  const watchedOtherIncome = watch('otherIncome');
  const watchedRetirementPension = watch('retirementPension');
  const watchedDisabilityPension = watch('disabilityPension');
  const watchedReversionaryPension = watch('reversionaryPension');

  // Calculer le total des revenus mensuels
  const totalMonthlyIncome = (watchedMonthlySalary || 0) +
                             (watchedOtherIncome || 0) +
                             (watchedRetirementPension || 0) +
                             (watchedDisabilityPension || 0) +
                             (watchedReversionaryPension || 0);

  const titleOptions = [
    { value: 'M', label: 'Monsieur' },
    { value: 'MME', label: 'Madame' },
    { value: 'MLE', label: 'Mademoiselle' }
  ];

  const contractTypeOptions = [
    { value: 'CDI', label: 'CDI' },
    { value: 'CDD', label: 'CDD' },
    { value: 'INT', label: 'Intérim' },
    { value: 'AUT', label: 'Autre' },
  ];

  // Actif = professionCode entre 1100 et 6300 (non inactif)
  const isActiveProfession = watchedProfessionCodeNum > 0 && watchedProfessionCodeNum < 7700;
  // Employeur uniquement si salarié : professionCode entre 3300 et 6300 (spec p.30)
  const canHaveEmployer = watchedProfessionCodeNum >= 3300 && watchedProfessionCodeNum <= 6300;
  // Type de contrat uniquement si salarié : professionCode entre 3300 et 6300 (spec p.41)
  const needsContractType = watchedProfessionCodeNum >= 3300 && watchedProfessionCodeNum <= 6300;
  // Ville employeur requise uniquement pour 3300, 3800, 6300 (spec Cofidis)
  const companyCityRequired = [3300, 3800, 6300].includes(watchedProfessionCodeNum);
  
  const onSubmit = (data: PersonalInfoFormData) => {
    // Mettre à jour toutes les données dans le store
    Object.entries(data).forEach(([key, value]) => {
      updateField(key, value);
    });
    
    goToNextStep();
  };
  
  const handleTitleChange = (value: string) => {
    setValue('title', value, { shouldValidate: true, shouldTouch: true });
    updateField('title', value);
  };
  
  const handleFieldChange = (fieldName: keyof PersonalInfoFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      updateField(fieldName, value);
    };

  const handleProfessionCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setValue('professionCode', value, { shouldValidate: true, shouldTouch: true });
    updateField('professionCode', value);
    // Réinitialiser contractTypeCode à CDI quand on change de profession
    const codeNum = parseInt(value || '0', 10);
    if (codeNum >= 3300 && codeNum <= 6300) {
      setValue('contractTypeCode', 'CDI', { shouldValidate: true });
      updateField('contractTypeCode', 'CDI');
    }
  };

  const handleProfessionalFieldChange = (fieldName: keyof PersonalInfoFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const numericFields = ['monthlySalary', 'seniorityMonths', 'otherIncome', 'retirementPension', 'disabilityPension', 'reversionaryPension'];
      const value = numericFields.includes(fieldName)
        ? (e.target.value === '' ? ('' as unknown as number) : Number(e.target.value))
        : e.target.value;
      setValue(fieldName, value, { shouldValidate: true });
      updateField(fieldName, value);
    };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Informations de l'emprunteur</CardTitle>
          <CardDescription>
            Informations personnelles et professionnelles de l'emprunteur
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

            {/* SECTION: État civil */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 border-b pb-2">État civil</h3>

              {/* Titre */}
              <div>
                <RadioGroup
                  label="Titre"
                  options={titleOptions}
                  value={watchedTitle}
                  onChange={handleTitleChange}
                  name="title"
                  required
                  error={fieldError('title') || errors.title}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Prénom */}
                <div>
                  <NameInput
                    value={formData.firstName || ''}
                    onChange={(e) => {
                      setValue('firstName', e.target.value, { shouldValidate: true });
                      updateField('firstName', e.target.value);
                    }}
                    onBlur={touchField('firstName')}
                    label="Prénom"
                    type="firstName"
                    required
                    error={fieldError('firstName') || errors.firstName}
                  />
                </div>

                {/* Nom */}
                <div>
                  <NameInput
                    value={formData.lastName || ''}
                    onChange={(e) => {
                      setValue('lastName', e.target.value, { shouldValidate: true });
                      updateField('lastName', e.target.value);
                    }}
                    onBlur={touchField('lastName')}
                    label="Nom de famille"
                    type="lastName"
                    required
                    error={fieldError('lastName') || errors.lastName}
                  />
                </div>
              </div>

              {/* Nom de naissance (si différent) */}
              <div>
                <NameInput
                  value={formData.birthName || ''}
                  onChange={(e) => {
                    setValue('birthName', e.target.value, { shouldValidate: true });
                    updateField('birthName', e.target.value);
                  }}
                  label="Nom de naissance (si différent du nom actuel)"
                  placeholder="Nom de naissance"
                  type="lastName"
                  error={fieldError('birthName') || errors.birthName}
                />
              </div>

              {/* Date de naissance */}
              <div>
                <TextField
                  {...register('birthDate')}
                  type="date"
                  label="Date de naissance"
                  required
                  error={fieldError('birthDate') || errors.birthDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setValue('birthDate', value, { shouldValidate: true, shouldTouch: true });
                    updateField('birthDate', value);
                  }}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                    .toISOString().split('T')[0]}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nationalité */}
                <div>
                  <SelectField
                    {...register('nationality')}
                    label="Nationalité"
                    options={getCountryOptions()}
                    required
                    error={fieldError('nationality') || errors.nationality}
                    onChange={handleFieldChange('nationality')}
                  />
                </div>

                {/* Pays de naissance */}
                <div>
                  <SelectField
                    {...register('birthCountry')}
                    label="Pays de naissance"
                    options={getCountryOptions()}
                    required
                    error={fieldError('birthCountry') || errors.birthCountry}
                    onChange={(e) => {
                      handleFieldChange('birthCountry')(e);
                      setValue('birthCity', '', { shouldValidate: true });
                      updateField('birthCity', '');
                      setValue('birthDepartment', '', { shouldValidate: true });
                      updateField('birthDepartment', '');
                      setBirthCityConfirmed(false);
                      setIsBirthCountryFR(e.target.value === 'FR');
                    }}
                  />
                </div>
              </div>

              {isBirthCountryFR && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Ville de naissance — uniquement si né en France */}
                  <div>
                    <CityAutocomplete
                      value={formData.birthCity || ''}
                      onChange={(value) => {
                        setValue('birthCity', value, { shouldValidate: true });
                        updateField('birthCity', value);
                        updateField('birthCityVerified', false);
                        setBirthCityConfirmed(false);
                      }}
                      onDepartmentExtracted={(departmentCode) => {
                        setValue('birthDepartment', departmentCode, { shouldValidate: true, shouldTouch: true });
                        setValue('birthCity', watch('birthCity'), { shouldTouch: true });
                        updateField('birthDepartment', departmentCode);
                        updateField('birthCityVerified', true);
                        setBirthCityConfirmed(true);
                      }}
                      onSelectionCleared={() => {
                        updateField('birthCityVerified', false);
                        setBirthCityConfirmed(false);
                      }}
                      onBlur={touchField('birthCity')}
                      label="Ville de naissance"
                      placeholder="Ville de naissance"
                      required
                      error={
                        touchedFields.birthCity && !birthCityConfirmed && (formData.birthCity || '').length >= 2
                          ? 'Veuillez sélectionner votre ville dans la liste'
                          : fieldError('birthCity') || errors.birthCity
                      }
                      helperText="Tapez votre ville et sélectionnez-la dans la liste"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION: Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 border-b pb-2">Contact</h3>

              {/* Email */}
              <div>
                <EmailInput
                  value={formData.email || ''}
                  onChange={(e) => {
                    setValue('email', e.target.value, { shouldValidate: true });
                    updateField('email', e.target.value);
                  }}
                  onBlur={touchField('email')}
                  label="Adresse email"
                  placeholder="votre@email.com"
                  required
                  error={fieldError('email') || errors.email}
                  helperText="Nous utiliserons cette adresse pour vous contacter"
                />
              </div>

              {/* Téléphone */}
              <div>
                <PhoneInput
                  value={formData.phone || ''}
                  onChange={(value) => {
                    setValue('phone', value, { shouldValidate: true });
                    updateField('phone', value);
                  }}
                  onBlur={touchField('phone')}
                  label="Numéro de téléphone"
                  required
                  error={fieldError('phone') || errors.phone}
                />
              </div>
            </div>

            {/* SECTION: Informations professionnelles */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 border-b pb-2">
                Informations professionnelles
              </h3>

              <p className="text-sm text-neutral-600">
                Parlez-nous de votre situation professionnelle et vos revenus
              </p>

              {/* Situation professionnelle — select avec optgroups (Annexe L) */}
              <div className="field-group">
                <label className="form-label">
                  Quelle est votre situation professionnelle ?
                  <span className="form-label-required">*</span>
                </label>
                <select
                  {...register('professionCode')}
                  className={`input-field ${touchedFields.professionCode && formErrors.professionCode ? 'input-error' : ''}`}
                  onChange={handleProfessionCodeChange}
                >
                  <option value="">-- Sélectionnez --</option>
                  <optgroup label="Indépendants">
                    <option value="1100">Artisans</option>
                    <option value="2100">Commerçants</option>
                    <option value="2200">Chefs d'entreprise</option>
                    <option value="2300">Professions agricoles indépendantes</option>
                    <option value="3100">Professions libérales</option>
                  </optgroup>
                  <optgroup label="Salariés">
                    <option value="3300">Cadres du privé</option>
                    <option value="3800">Professions intermédiaires du privé</option>
                    <option value="4500">Autres professions du public</option>
                    <option value="4600">Autres professions du privé</option>
                    <option value="5200">Employés du public</option>
                    <option value="5300">Employés du privé</option>
                    <option value="5400">Professions agricoles salariées</option>
                    <option value="5500">Personnels de maison</option>
                    <option value="5600">Apprentis, stagiaires</option>
                    <option value="6300">Ouvriers</option>
                  </optgroup>
                  <optgroup label="Inactifs">
                    <option value="7700">Retraités, pré-retraités</option>
                    <option value="8100">Demandeurs d'emploi</option>
                    <option value="8400">Étudiants</option>
                    <option value="8500">Autres inactifs</option>
                  </optgroup>
                </select>
                {touchedFields.professionCode && formErrors.professionCode && (
                  <p className="error-message">{formErrors.professionCode.message}</p>
                )}
              </div>

              {/* Type de contrat — uniquement pour salariés (3300–6300) */}
              {needsContractType && (
                <div>
                  <RadioGroup
                    label="Type de contrat"
                    options={contractTypeOptions}
                    value={watchedContractTypeCode}
                    onChange={(value) => {
                      setValue('contractTypeCode', value, { shouldValidate: true, shouldTouch: true });
                      updateField('contractTypeCode', value);
                    }}
                    name="contractTypeCode"
                    required
                    error={fieldError('contractTypeCode')}
                  />
                </div>
              )}

              {/* Revenus mensuels — uniquement pour les actifs (professionCode <= 6300) */}
              {isActiveProfession && (
                <div>
                  <CurrencyInput
                    value={watchedMonthlySalary || 0}
                    onChange={(value) => {
                      setValue('monthlySalary', value, { shouldValidate: true });
                      updateField('monthlySalary', value);
                    }}
                    onBlur={touchField('monthlySalary')}
                    label="Revenus mensuels nets"
                    placeholder="1 500 €"
                    required
                    error={fieldError('monthlySalary') || errors.monthlySalary}
                    helperText="Indiquez vos revenus nets mensuels en euros"
                    step={100}
                    showProgress={false}
                  />
                </div>
              )}

              {isActiveProfession && (
                <>
                  {/* Ancienneté et Nom de l'entreprise sur la même ligne */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Ancienneté (1/3) */}
                    <div className="sm:col-span-1">
                      <div className="field-group">
                        <label className="form-label">
                          Ancienneté
                          <span className="form-label-required">*</span>
                        </label>
                        <div className="relative">
                          <input
                            {...register('seniorityMonths', { valueAsNumber: true })}
                            type="number"
                            placeholder="24"
                            className={`input-field pr-16 ${touchedFields.seniorityMonths && formErrors.seniorityMonths ? 'input-error' : ''}`}
                            onChange={(e) => {
                              if (e.target.value === '') {
                                setValue('seniorityMonths', '' as unknown as number, { shouldValidate: true });
                                updateField('seniorityMonths', '' as unknown as number);
                                return;
                              }
                              const capped = Math.min(Number(e.target.value), maxSeniorityMonths);
                              e.target.value = String(capped);
                              setValue('seniorityMonths', capped, { shouldValidate: true });
                              updateField('seniorityMonths', capped);
                            }}
                            min={0}
                            max={maxSeniorityMonths}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500">
                            mois
                          </div>
                        </div>
                        {watchedSeniorityMonths > 0 && (
                          <p className="helper-text">
                            ≈ {(watchedSeniorityMonths / 12) < 1
                              ? 'Moins d\'1 an'
                              : `${Math.round(watchedSeniorityMonths / 12 * 10) / 10} an${Math.round(watchedSeniorityMonths / 12 * 10) / 10 >= 2 ? 's' : ''}`}
                          </p>
                        )}
                        {touchedFields.seniorityMonths && formErrors.seniorityMonths && (
                          <p className="error-message">{formErrors.seniorityMonths.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Nom de l'entreprise (2/3) — uniquement pour salariés (3300–6300) */}
                    {canHaveEmployer && (
                      <div className="sm:col-span-2">
                        <TextField
                          {...register('companyName')}
                          label="Employeur"
                          placeholder="Ex: SARL Martin & Associés"
                          error={fieldError('companyName') || errors.companyName}
                          onChange={handleProfessionalFieldChange('companyName')}
                        />
                      </div>
                    )}
                  </div>

                  {/* Ville de l'employeur — uniquement pour salariés (3300–6300) */}
                  {canHaveEmployer && (
                    <div>
                      <CityAutocomplete
                        value={formData.companyCity || ''}
                        onChange={(value) => {
                          setValue('companyCity', value, { shouldValidate: true });
                          updateField('companyCity', value);
                          setCompanyCityConfirmed(false);
                        }}
                        onCitySelect={(data) => {
                          setValue('companyPostalCode', data.postcode, { shouldValidate: true });
                          updateField('companyPostalCode', data.postcode);
                          setValue('companyCity', data.city, { shouldValidate: true });
                          updateField('companyCity', data.city);
                          setCompanyCityConfirmed(true);
                        }}
                        onDepartmentExtracted={(departmentCode) => {
                          setValue('companyRegionCode', departmentCode, { shouldValidate: true });
                          updateField('companyRegionCode', departmentCode);
                        }}
                        onSelectionCleared={() => setCompanyCityConfirmed(false)}
                        onBlur={touchField('companyCity')}
                        label="Ville de l'employeur"
                        placeholder="Paris, Lyon..."
                        required={companyCityRequired}
                        error={
                          touchedFields.companyCity && !companyCityConfirmed && (formData.companyCity || '').length >= 2
                            ? 'Veuillez sélectionner la ville dans la liste'
                            : fieldError('companyCity') || errors.companyCity
                        }
                        helperText={companyCityRequired ? 'Tapez votre ville et sélectionnez-la dans la liste' : undefined}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Date de fin de contrat (CDD/INT uniquement) */}
              {['CDD', 'INT'].includes(watchedContractTypeCode) && (
                <div>
                  <TextField
                    {...register('contractEndDate')}
                    type="date"
                    label="Date de fin de contrat"
                    required
                    error={fieldError('contractEndDate') || errors.contractEndDate}
                    onChange={handleProfessionalFieldChange('contractEndDate')}
                    helperText="Date de fin prévue de votre contrat"
                  />
                </div>
              )}

              {/* Pensions */}
              <div className="space-y-4 pt-4 border-t border-neutral-200">
                <h4 className="text-sm font-semibold text-neutral-700">
                  Pensions
                </h4>

                {/* Pension de retraite */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <CurrencyInput
                    value={watchedRetirementPension || 0}
                    onChange={(value) => { setValue('retirementPension', value, { shouldValidate: true }); updateField('retirementPension', value); }}
                    label="Pension de retraite"
                    placeholder="1 000 €"
                    error={formErrors.retirementPension?.message}
                    min={0} step={50} showProgress={false}
                  />
                  <div className="field-group">
                    <label className="form-label">Fréquence</label>
                    <select {...register('retirementPensionPeriodicity')} className="input-field" onChange={handleProfessionalFieldChange('retirementPensionPeriodicity')}>
                      <option value="M">Mensuelle</option>
                      <option value="T">Trimestrielle</option>
                      <option value="S">Semestrielle</option>
                      <option value="A">Annuelle</option>
                    </select>
                  </div>
                </div>

                {/* Pension d'invalidité */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <CurrencyInput
                    value={watchedDisabilityPension || 0}
                    onChange={(value) => { setValue('disabilityPension', value, { shouldValidate: true }); updateField('disabilityPension', value); }}
                    label="Pension d'invalidité"
                    placeholder="1 000 €"
                    error={formErrors.disabilityPension?.message}
                    min={0} step={50} showProgress={false}
                  />
                  <div className="field-group">
                    <label className="form-label">Fréquence</label>
                    <select {...register('disabilityPensionPeriodicity')} className="input-field" onChange={handleProfessionalFieldChange('disabilityPensionPeriodicity')}>
                      <option value="M">Mensuelle</option>
                      <option value="T">Trimestrielle</option>
                      <option value="S">Semestrielle</option>
                      <option value="A">Annuelle</option>
                    </select>
                  </div>
                </div>

                {/* Pension de réversion */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <CurrencyInput
                    value={watchedReversionaryPension || 0}
                    onChange={(value) => { setValue('reversionaryPension', value, { shouldValidate: true }); updateField('reversionaryPension', value); }}
                    label="Pension de réversion"
                    placeholder="1 000 €"
                    error={formErrors.reversionaryPension?.message}
                    min={0} step={50} showProgress={false}
                  />
                  <div className="field-group">
                    <label className="form-label">Fréquence</label>
                    <select {...register('reversionaryPensionPeriodicity')} className="input-field" onChange={handleProfessionalFieldChange('reversionaryPensionPeriodicity')}>
                      <option value="M">Mensuelle</option>
                      <option value="T">Trimestrielle</option>
                      <option value="S">Semestrielle</option>
                      <option value="A">Annuelle</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Messages informatifs selon le statut */}
              {watchedContractTypeCode === 'CDI' && needsContractType && (
                <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                  <h3 className="font-semibold text-success-700 mb-2">
                    CDI - Excellente stabilité
                  </h3>
                  <p className="text-sm text-success-700">
                    Le CDI est généralement très apprécié par les organismes de crédit.
                  </p>
                </div>
              )}

              {watchedProfessionCodeNum === 3100 && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <h3 className="font-semibold text-primary-800 mb-2">
                    Profession libérale / Indépendant
                  </h3>
                  <p className="text-sm text-primary-700">
                    Des justificatifs supplémentaires pourront être demandés
                    (bilans, déclarations fiscales, etc.)
                  </p>
                </div>
              )}

              {totalMonthlyIncome > 0 && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                  <h3 className="font-semibold text-neutral-800 mb-2">
                    💰 Capacité de remboursement
                  </h3>
                  <p className="text-sm text-neutral-700">
                    Revenus déclarés: <strong>{totalMonthlyIncome.toLocaleString('fr-FR')} € / mois</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Ajouter un co-emprunteur */}
            {['C', 'D'].includes(formData.familyStatus || '') ? (
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6">
                <h3 className="font-semibold text-neutral-500 mb-2">Co-emprunteur</h3>
                <p className="text-sm text-neutral-400">
                  Un co-emprunteur n'est pas possible pour une personne {formData.familyStatus === 'C' ? 'célibataire' : 'divorcée'}.
                </p>
              </div>
            ) : (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-primary-900 mb-2">
                      Co-emprunteur
                    </h3>
                    <p className="text-sm text-primary-700 mb-4">
                      Vous pouvez ajouter un co-emprunteur (conjoint, partenaire) pour augmenter vos chances d'acceptation.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      updateField('hasCoBorrower', !formData.hasCoBorrower);
                    }}
                    className={`flex-1 px-6 py-3 font-medium rounded-lg transition-colors ${
                      formData.hasCoBorrower
                        ? 'bg-success-600 text-white hover:bg-success-700'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {formData.hasCoBorrower ? '✓ Co-emprunteur ajouté' : 'Ajouter un co-emprunteur'}
                  </button>
                  {formData.hasCoBorrower && (
                    <button
                      type="button"
                      onClick={() => {
                        updateField('hasCoBorrower', false);
                      }}
                      className="px-6 py-3 font-medium rounded-lg transition-colors bg-red-600 text-white hover:bg-red-700"
                    >
                      Retirer
                    </button>
                  )}
                </div>
                {formData.hasCoBorrower && (
                  <p className="text-sm text-success-700 mt-3 text-center">
                    Vous pourrez renseigner les informations du co-emprunteur plus tard
                  </p>
                )}
              </div>
            )}
            
            <FormNavigation
              onPrevious={goToPreviousStep}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={
                isValid &&
                (birthCityConfirmed || !isBirthCountryFR) &&
                (!companyCityRequired || companyCityConfirmed) &&
                (!isActiveProfession || Number(watchedMonthlySalary) >= 1) &&
                (!isActiveProfession || (
                  watch('seniorityMonths') !== undefined &&
                  !isNaN(Number(watch('seniorityMonths'))) &&
                  watch('seniorityMonths') !== ('' as unknown as number)
                ))
              }
              nextButtonText="Continuer"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}